import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Switch,
  Button,
  Table,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Empty,
  Alert,
  message,
} from 'antd';
import { MedicineBoxOutlined, PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  TreatmentIntent,
  TreatmentStatus,
  type CreateTreatmentPlanDto,
  type TreatmentPlan,
} from '../../../../api/dermatoApi';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';
import { useDocumentLanguage } from '../../../../hooks/useDocumentLanguage';
import {
  COMMISSION_NOT_RECORDED_TOOLTIP,
  DOCUMENT_FALLBACK_MESSAGE,
  downloadMedicalDocument,
} from '../../medicalDocuments';

const { Text } = Typography;

const INTENT_OPTIONS = Object.values(TreatmentIntent).map((v) => ({
  value: v,
  label:
    v === 'CURATIVE' ? 'Curativ'
    : v === 'PALLIATIVE' ? 'Paliativ'
    : v === 'ADJUVANT' ? 'Adjuvant'
    : v === 'NEOADJUVANT' ? 'Neoadjuvant'
    : 'Profilactic',
}));

const STATUS_OPTIONS = Object.values(TreatmentStatus).map((v) => ({
  value: v,
  label:
    v === 'PLANNED' ? 'Planificat'
    : v === 'SCHEDULED' ? 'Programat'
    : v === 'IN_PROGRESS' ? 'În curs'
    : v === 'COMPLETED' ? 'Finalizat'
    : 'Anulat',
}));

const Step12Treatment = () => {
  const { form, lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const { language } = useDocumentLanguage();
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TreatmentPlan | null>(null);
  const [saving, setSaving] = useState(false);
  const [formAntd] = Form.useForm<CreateTreatmentPlanDto>();
  const [downloadingCommission, setDownloadingCommission] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  const refresh = async () => {
    if (!lesion?.id) return;
    setLoadingList(true);
    try {
      const list = await dermatoApi.treatments.getByLesion(lesion.id);
      setPlans(list);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [lesion?.id]);

  const openCreate = () => {
    setEditing(null);
    formAntd.resetFields();
    formAntd.setFieldsValue({
      decisionDate: dayjs(),
      intent: TreatmentIntent.CURATIVE,
      status: TreatmentStatus.PLANNED,
      treatmentType: 'Excizie chirurgicală',
      decisionText: '',
      consentObtained: false,
      excisionChosen: true,
    } as any);
    setModalOpen(true);
  };

  const openEdit = (p: TreatmentPlan) => {
    setEditing(p);
    formAntd.setFieldsValue({
      ...p,
      decisionDate: dayjs(p.decisionDate),
      scheduledDate: p.scheduledDate ? dayjs(p.scheduledDate) : undefined,
    } as any);
    setModalOpen(true);
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!lesion?.id) return false;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateTreatmentPlanDto = {
        ...values,
        decisionDate: (values.decisionDate as unknown as dayjs.Dayjs).toISOString(),
        scheduledDate: values.scheduledDate ? (values.scheduledDate as unknown as dayjs.Dayjs).toISOString() : undefined,
      };
      if (editing?.id) {
        await dermatoApi.treatments.update(editing.id, dto);
      } else {
        await dermatoApi.treatments.createForLesion(lesion.id, dto);
      }
      setModalOpen(false);
      await refresh();
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Salvarea pasului are loc în modal; în afara modalului nu există modificări nesalvate.
  const saveCurrentStep = async (): Promise<boolean> => {
    if (!modalOpen) return true;
    return handleSubmit();
  };

  useRegisterStepSave(saveCurrentStep);

  const handleDelete = async (id: string) => {
    await dermatoApi.treatments.delete(id);
    await refresh();
  };

  /**
   * §1.2.6 — fișa comisiei oncologice. Disponibilă doar dacă decizia comisiei
   * e înregistrată în `TreatmentPlan.commissionOutcomeSummary` (§1.3).
   */
  const commissionRecorded = useMemo(
    () => plans.some((p) => Boolean(p.commissionOutcomeSummary?.trim())),
    [plans],
  );

  const handleCommissionSheet = async () => {
    if (!form?.id || !lesion?.id) return;
    try {
      setDownloadingCommission(true);
      setFallbackMessage(null);
      const result = await downloadMedicalDocument('commission-sheet', {
        formId: form.id,
        lang: language,
        lesionId: lesion.id,
      });
      if (result.meta.fallback) {
        setFallbackMessage(result.meta.message ?? DOCUMENT_FALLBACK_MESSAGE);
      }
      message.success('Fișa comisiei oncologice a fost descărcată.');
    } catch {
      message.error('Eroare la descărcarea fișei comisiei oncologice.');
    } finally {
      setDownloadingCommission(false);
    }
  };

  if (loading) return <Card loading title="Tratament" />;

  if (lesions.length === 0) {
    return (
      <Card title="Tratament">
        <Empty description="Nu există leziuni. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <MedicineBoxOutlined style={{ color: '#1890ff' }} />
          <span>Plan terapeutic — 1:N per leziune</span>
        </Space>
      }
      extra={
        lesion && (
          <Space>
            <span title={commissionRecorded ? undefined : COMMISSION_NOT_RECORDED_TOOLTIP}>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => void handleCommissionSheet()}
                loading={downloadingCommission}
                disabled={!commissionRecorded || !form?.id}
              >
                Fișă comisie oncologică
              </Button>
            </span>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Adaugă plan
            </Button>
          </Space>
        )
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {fallbackMessage && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Limba documentului a fost ajustată"
          description={fallbackMessage}
          data-testid="commission-fallback-alert"
        />
      )}

      {!lesion && <Alert type="warning" showIcon message="Selectează o leziune." />}

      {lesion && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text type="secondary">({plans.length} planuri)</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          {loadingList ? (
            <Alert type="info" message="Se încarcă planurile…" />
          ) : plans.length === 0 ? (
            <Empty description="Niciun plan terapeutic." />
          ) : (
            <Table
              scroll={{ x: 'max-content' }}
              rowKey="id"
              dataSource={plans}
              size="small"
              pagination={false}
              columns={[
                { title: 'Data decizie', dataIndex: 'decisionDate', render: (v: string) => new Date(v).toLocaleDateString('ro-RO') },
                { title: 'Tip tratament', dataIndex: 'treatmentType' },
                { title: 'Intenție', dataIndex: 'intent', render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color="blue">{v}</Tag> },
                {
                  title: 'Programat',
                  dataIndex: 'scheduledDate',
                  render: (v: string | null) => (v ? new Date(v).toLocaleDateString('ro-RO') : '—'),
                },
                { title: 'Consimțământ', dataIndex: 'consentObtained', render: (v: boolean) => (v ? '✓' : '✗') },
                {
                  title: '',
                  width: 100,
                  render: (_: unknown, p: TreatmentPlan) => (
                    <Space size="small">
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(p)} />
                      <Popconfirm title="Șterge plan?" onConfirm={() => void handleDelete(p.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </>
      )}

      <Modal
        title={editing ? 'Editare plan terapeutic' : 'Plan terapeutic nou'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => void handleSubmit()}
        confirmLoading={saving}
        okText={editing ? 'Salvează' : 'Creează'}
        cancelText="Anulează"
        destroyOnClose
      >
        <Form form={formAntd} layout="vertical" preserve={false}>
          <Form.Item name="decisionDate" label="Data decizie" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="treatmentType" label="Tip tratament" rules={[{ required: true }]}>
            <Input placeholder="ex: Excizie chirurgicală, Imunoterapie, Chimioterapie topică" />
          </Form.Item>
          <Form.Item name="intent" label="Intenție" rules={[{ required: true }]}>
            <Select options={INTENT_OPTIONS} />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name="scheduledDate" label="Data programată">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="team" label="Echipă">
            <Input placeholder="ex: Dr. X + Dr. Y" />
          </Form.Item>
          <Form.Item name="excisionChosen" label="Excizie aleasă" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="immunotherapyChosen" label="Imunoterapie aleasă" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="otherTreatmentChosen" label="Alt tratament ales" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="consentObtained" label="Consimțământ obținut" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="observations" label="Observații">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="decisionText" label="Decizie (text)" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Descrie decizia luată în comisia multidisciplinară…" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Step12Treatment;
